import { useContext, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Button, Card, Container, IconButton, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Iconify from "../../../minimals/components/iconify";
import Label from "../../../minimals/components/label/Label";
import CustomBreadcrumbs from "../../components/custom-breadcrumbs";
import { useSettingsContext } from "../../contexts/SettingsContext";
import { getLogo } from "../../utils/getLogo";
import { ProfileCover } from "../shared/profile";
import { CompanyContext } from "./CompanyContext";
import { TABS } from "./TABS";
import { closeIFrame } from "../../utils/iframeUtils";
import FeedbackButton from "../../shared/FeedbackButton";
import { UserContext } from "../../contexts/UserContext";
import FeedbackModal from "../../shared/FeedbackModal";
import { createPortal } from "react-dom";
import { Global, css } from "@emotion/react";
import useResponsive from "../../../minimals/hooks/useResponsive";
import { HeaderSlotContext } from "../../components/layouts/dashboard/HeaderSlotContext";

export default function Companies() {
  const { themeStretch, isAppInIFrame } = useSettingsContext();
  const navigate = useNavigate();
  const { section } = useParams();
  const { user } = useContext(UserContext);
  const searchParams = new URLSearchParams(location.search);
  const idFromUrl = searchParams.get("id");
  const { company, isFetching } = useContext(CompanyContext);
  const isMobile = useResponsive("down", "md");
  const { slotEl } = useContext(HeaderSlotContext);
  // The desktop feedback tab is fixed and rotated down the right edge, straight
  // over the content on a phone.
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const coverRef = useRef<HTMLDivElement | null>(null);
  const [isCoverVisible, setIsCoverVisible] = useState(true);

  useEffect(() => {
    if (!isMobile || !coverRef.current) {
      setIsCoverVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setIsCoverVisible(entry.isIntersecting), {
      // Treat "behind the fixed header" as gone, not just off-screen.
      rootMargin: "-64px 0px 0px 0px"
    });
    observer.observe(coverRef.current);
    return () => observer.disconnect();
  }, [isMobile, company?.id]);

  const selectedValue = () => {
    return TABS.find((tab) => tab.values.some((value) => value === section))?.values[0];
  };

  if (isFetching) {
    return <></>;
  }

  ////If this gets use more frequently, it can be styled much better
  if (!company) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 144px)"
        }}
      >
        <div style={{ textAlign: "center", fontSize: "50px", fontWeight: "" }}>Company Not Found In Phonebook</div>
      </div>
    );
  }

  return (
    <>
      {isAppInIFrame && (
        <Stack direction="row" justifyContent="flex-end" paddingRight={1.5} marginBottom={1}>
          <IconButton
            size="large"
            onClick={closeIFrame}
            sx={{ position: "absolute", top: 16, right: 16, padding: 0.3 }}
          >
            <Iconify icon="eva:close-outline" width={32} height={32} />
          </IconButton>
        </Stack>
      )}
      <Helmet>
        <title> Company Profile | TitleQ</title>
      </Helmet>

      {isMobile && (
        <Global
          styles={css`
            main {
              /* Mobile drops the breadcrumb block, which was supplying the clearance
                 under the fixed 64px header. */
              padding-top: 64px !important;
            }
          `}
        />
      )}

      {/* Heading and actions render in the site header beside the nav button; once
          the cover scrolls away the title becomes the company's name. */}
      {isMobile && slotEl
        ? createPortal(
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: 1, minWidth: 0, gap: 1 }}
            >
              <Typography
                noWrap
                sx={{ color: "primary.dark", fontSize: 17, fontWeight: 700, lineHeight: 1.2, minWidth: 0 }}
              >
                {!isCoverVisible && company?.name ? company.name : "Company Profile"}
              </Typography>
              <Stack direction="row" alignItems="center" sx={{ flexShrink: 0, gap: 1 }}>
                <IconButton
                  size="small"
                  aria-label="Submit contact update"
                  onClick={() => setIsFeedbackOpen(true)}
                  sx={{ border: "1px solid", borderColor: "divider" }}
                >
                  <Iconify icon="material-symbols:rate-review-outline" width={18} />
                </IconButton>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!company.isActive}
                  startIcon={<Iconify icon="eva:edit-fill" width={16} />}
                  onClick={() => navigate(`../phonebook/edit-company?id=${company?.id || idFromUrl}`)}
                  sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                >
                  Edit
                </Button>
              </Stack>
            </Stack>,
            slotEl
          )
        : null}

      <Container maxWidth={themeStretch ? false : "lg"}>
        {company && !company.isActive && (
          <Label variant="soft" color="error">
            Inactive
          </Label>
        )}
        {!isMobile && (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <CustomBreadcrumbs
              heading={`${isAppInIFrame ? "Phonebook - " : ""}Company Profile`}
              links={[{ name: "Companies", href: "../phonebook/contacts/companies" }, { name: company?.name }]}
            />
            <Button
              disabled={!company.isActive}
              variant="contained"
              startIcon={<Iconify icon="eva:edit-fill" />}
              onClick={() => navigate(`../phonebook/edit-company?id=${company?.id || idFromUrl}`)}
            >
              Edit
            </Button>
          </Stack>
        )}
        {(() => {
          const tabsElement = (
            <Tabs
              value={selectedValue()}
              // Icon+label tabs don't fit a phone; let them scroll rather than squash.
              variant={isMobile ? "scrollable" : "standard"}
              allowScrollButtonsMobile={isMobile}
              onChange={(event, newValue) => {
                navigate(`../phonebook/company/${newValue}?id=${company?.id || idFromUrl}`);
              }}
              sx={{
                width: isMobile ? undefined : 1,
                bgcolor: "background.paper",
                ...(isMobile
                  ? {
                      // Sticky rather than pinned to the card: the cover scrolls away,
                      // the tabs stay under the 64px header, and the header title takes
                      // over showing which company this is.
                      position: "sticky",
                      top: 64,
                      zIndex: 10,
                      mb: 2,
                      // Break out of the Container's gutter so the strip runs edge to
                      // edge and reads as an extension of the header, not as a piece
                      // of the card below.
                      mx: { xs: -2, sm: -3 },
                      width: "auto",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      transition: "box-shadow 150ms",
                      boxShadow: isCoverVisible ? "none" : "0 8px 12px -8px rgba(22, 28, 36, 0.36)"
                    }
                  : {
                      bottom: 0,
                      zIndex: 9,
                      position: "absolute"
                    }),
                "& .MuiTabs-flexContainer": {
                  pl: { xs: 2, md: 0 },
                  pr: { md: 3 },
                  justifyContent: {
                    sm: "center",
                    md: "flex-end"
                  }
                }
              }}
            >
              {TABS.map((tab) => (
                <Tab key={tab.values[0]} value={tab.values[0]} icon={tab.icon} label={tab.label} />
              ))}
            </Tabs>
          );

          const coverCard = (
            <Card
              ref={coverRef}
              sx={{
                mb: { xs: 0, md: 3 },
                // Desktop's 280 includes the tab strip overlaid at its bottom edge;
                // on mobile the tabs have moved out, so this is cover only.
                height: { xs: 110, md: 280 },
                position: "relative"
              }}
            >
              <ProfileCover
                name={company?.name || ""}
                logo={getLogo(company?.website)}
                companyType={company?.companyType?.name}
              />
              {!isMobile && tabsElement}
            </Card>
          );

          return (
            <>
              {coverCard}
              {isMobile && tabsElement}
            </>
          );
        })()}
        {!isMobile && (
          <FeedbackButton
            url={window.location.href}
            destinationEmail={"phonebook@madisoncres.com"}
            subject="New phonebook update submitted"
            placeholder="Updated contact information"
            buttonText="Submit Contact Update"
          />
        )}
        {TABS.map((tab) => tab.values.some((v) => v === section) && <Box key={tab.values[0]}> {tab.component}</Box>)}
      </Container>

      {isFeedbackOpen && (
        <FeedbackModal
          onClose={() => setIsFeedbackOpen(false)}
          open={isFeedbackOpen}
          url={window.location.href}
          destinationEmail={"phonebook@madisoncres.com"}
          subject="New phonebook update submitted"
          placeholder="Updated contact information"
        />
      )}
    </>
  );
}
